import React, { ReactElement, useEffect, useState } from "react";
import { useTheme } from "styled-components";
import { Box, HBox, VBox } from "../box/Box";
import Spacer from "../spacer/Spacer";
import { Typo } from "../text/Typo";
import { ITheme } from "../theme/GlobalTheme";
import { useWindowSize } from "../utils/useResize";
import { TabStrip } from "./TabStrip";

// STYLES
export const getDefaultTabsLightTheme = {
	backgroundHover: "rgb(206, 215, 235)",
};

export const getDefaultTabsDarkTheme: typeof getDefaultTabsLightTheme = {
	backgroundHover: "rgb(206, 215, 235)",
};

export interface ITabsProps {
	children?: (ReactElement<ITabItemProps> | null | false)[] | ReactElement<ITabItemProps>;
	selected?: number;
	sx?: React.CSSProperties;
	scrollable?: boolean;
	background?: string;
	spaceLeft?: number;
	spaceBottom?: number;
	rounds?: boolean;
	decoration?: React.ReactElement;
	title?: string;
	onSectionSelect?: (section: number) => void;
	checkFeature?: (feature: string) => boolean;
	variant?: "modern" | "round" | "strong";
}

export interface ITabItemProps {
	title: string;
	children?: any;
	section?: number;
	type?: "default" | "spacer" | "button";
	onClick?: () => void;
	sx?: React.CSSProperties;
	/** Icon to render before the title in modern-variant tabs. Pass any React node
	 *  (e.g. a lucide-react icon: `<CreditCard size={16} />`). Other variants ignore it. */
	icon?: React.ReactNode;
}

export const Tabs = ({
	children,
	scrollable,
	background,
	selected,
	sx,
	spaceLeft,
	spaceBottom,
	rounds,
	decoration,
	title,
	checkFeature,
	onSectionSelect,
	variant,
}: ITabsProps) => {
	if (selected === undefined || isNaN(selected)) selected = 0;

	if (children) {
		if (!Array.isArray(children) && React.isValidElement(children)) children = [children];
	}

	const hiddenChilds: any[] = [];

	if (children && checkFeature) {
		children.map(val => {
			if (!val) return null;
			React.Children.toArray(val.props.children).forEach(child => {
				if (!React.isValidElement(child)) return true;
				const component = child.type;
				const feature = (component as any).feature;
				if (feature) {
					// check feature
					if (checkFeature && !checkFeature(feature)) {
						hiddenChilds.push(val);
					}
				}
			});
		});

		//selected = undefined;

		if (hiddenChilds.length > 0 && children.length > 0) {
			for (let index = 0; index < children.length; index++) {
				if (!hiddenChilds.find(fval => fval === children[index])) {
					selected = index;
					break;
				}
			}
		}
	}

	const [currentSelection, setCurrentSelection] = useState(0);

	useEffect(() => {
		if (selected !== undefined) {
			// todo need this line?
			onSelectChange(selected);
			setCurrentSelection(selected);
		}
	}, [selected]);

	const theme = useTheme() as ITheme;
	const style = theme.tabs;
	const { width: viewportWidth } = useWindowSize();
	const isMobile = viewportWidth <= 768;

	if (!children) return null;

	const onSelectChange = (index: number) => {
		for (let i = 0; i < children.length; i++) {
			let child = children[i];
			if (child && typeof child === "object" && "props" in child) {
				if (child.props.type === "spacer" || child.props.type === "button") {
					return;
				}
			}
		}

		setCurrentSelection(index);

		if (children.length > index) {
			if (!children[index] || !children[index].props) return;
			if (!React.isValidElement(children[index])) return;
			const { section } = children[index].props;
			onSectionSelect && onSectionSelect(section ?? index);
		}
	};

	let tabs = null;
	if (rounds || variant === "round") {
		tabs = children?.map((val, index) => {
			if (!val) return null;
			if (hiddenChilds && hiddenChilds.find(fval => fval === val)) return null;
			const props = val.props as ITabItemProps;

			if (props.type === "spacer") {
				return <Spacer key={index} grow />;
			}

			return (
				<HBox
					key={index}
					sx={{
						padding: "6px 16px",
						cursor: "pointer",
						overflow: "hidden",
						backgroundColor: currentSelection === index ? "#1f3344" : "transparent",
						border: currentSelection === index ? "1px solid rgba(0,0,0,0)" : "1px solid rgba(0,0,0,.1)",
						borderRadius: "16px",
						transition: "background-color .3s, border .3s, color .3s",
						"&:hover": {
							backgroundColor: currentSelection === index ? undefined : "tabsHoverColor",
						},
						...props.sx,
					}}
					onClick={() => {
						if (props.onClick) {
							props.onClick();
							return;
						}

						if (props.type !== "spacer" && props.type !== "button") {
							onSelectChange(index);
							setCurrentSelection(index);
						}
					}}>
					<Typo
						color={currentSelection === index ? "white" : "textPrimary"}
						sx={{
							whiteSpace: "nowrap",
							textOverflow: "ellipsis",
							fontSize: ".8rem",
							overflow: "hidden",
						}}>
						{val.props && val.props.title}
					</Typo>
				</HBox>
			);
		});
	} else {
		tabs = children?.map((val, index) => {
			if (!val) return null;

			if (hiddenChilds && hiddenChilds.find(fval => fval === val)) return null;

			const isModern = variant === "modern";
			const isStrong = variant === "strong";
			const isSelected = currentSelection === index;
			const tabIcon = (val.props as ITabItemProps)?.icon;
			return (
				<HBox
					key={index}
					sx={{
						gap: isModern && tabIcon ? "8px" : undefined,
						alignItems: "center",
						padding: isMobile ? "10px 14px" : isStrong ? "12px 24px" : isModern ? "11px 20px" : "15px 20px",
						cursor: "pointer",
						position: isModern ? ("relative" as const) : undefined,
						// Modern: underline lives in `::after` so we can animate its scaleX in/out
						// smoothly. Non-modern variants keep the old border-bottom (no animation).
						borderBottom: isModern
							? "1px solid rgba(0,0,0,.05)"
							: isSelected
								? isStrong ? "3px solid textPrimary" : "2px solid textPrimary"
								: isStrong ? "3px solid rgba(0,0,0,.1)" : "2px solid rgba(0,0,0,.1)",
						borderTopLeftRadius: isModern ? "6px" : undefined,
						borderTopRightRadius: isModern ? "6px" : undefined,
						color: isSelected ? "textPrimary" : "textSecondary",
						transition: "background-color .15s, color .15s, transform .18s",
						overflow: isMobile ? "visible" : "hidden",
						flexShrink: 0,
						"&:hover": {
							backgroundColor: isSelected ? undefined : style?.backgroundHover,
							color: "textPrimary",
							// Subtle "lift" on hover for modern — feels touchable without being playful.
							transform: isModern && !isSelected ? "translateY(-1px)" : undefined,
						},
						// Animated underline: a pseudo-element below the tab that grows in / shrinks
						// out via scaleX. transform-origin keeps the animation centered on the tab,
						// so it expands from the middle. 3px thickness matches the previous static
						// underline for visual continuity.
						...(isModern
							? {
								"&::after": {
									content: '""',
									position: "absolute",
									left: 0,
									right: 0,
									bottom: "-1px",
									height: "3px",
									backgroundColor: "textPrimary",
									transform: isSelected ? "scaleX(1)" : "scaleX(0)",
									transformOrigin: "center",
									transition: "transform .22s cubic-bezier(.4, 0, .2, 1)",
									borderRadius: "2px 2px 0 0",
								},
							}
							: {}),
					}}
					onClick={() => {
						onSelectChange(index);
						setCurrentSelection(index);
					}}>
					{isModern && tabIcon && (
						<Box
							sx={{
								display: "inline-flex",
								alignItems: "center",
								color: "inherit",
								// Selected icon nudged up by ~10% — emphasis without obvious "growing".
								transform: isSelected ? "scale(1.08)" : "scale(1)",
								transition: "transform .18s ease, color .15s",
							}}>
							{tabIcon}
						</Box>
					)}
					<Typo
						block
						bold={isStrong ? true : undefined}
						sx={{
							whiteSpace: "nowrap",
							textOverflow: "ellipsis",
							overflow: "hidden",
							fontSize: isModern ? ".85rem" : isStrong ? ".85rem" : ".9rem",
							...(isModern ? { fontWeight: isSelected ? 600 : 500, letterSpacing: "0.01em" } : {}),
							...(isStrong ? { textTransform: "uppercase", letterSpacing: "0.03em" } : {}),
						}}>
						{val.props && val.props.title}
					</Typo>
				</HBox>
			);
		});
	}

	if (typeof children[currentSelection] === "boolean") return null;

	let current = currentSelection !== undefined && children[currentSelection]?.props?.children;
	if (!current) return null;

	if (scrollable) {
		current = (
			<Box
				sx={{
					overflow: "auto",
					flexGrow: 1,
					display: "flex",
					flexDirection: "column",
				}}>
				{current}
			</Box>
		);
	} else if (sx) {
		current = <Box>{current}</Box>;
	}

	if (rounds || variant === "round") {
		return (
			<>
				<HBox
					sx={{
						background: background ? background : "transparent",
						marginLeft: spaceLeft ? `${spaceLeft}px` : undefined,
						...sx,
					}}>
					<VBox sx={{ gap: "8px" }}>
						{title && (
							<>
								<Typo variant="small" color="textSecondary" block>
									{title}
								</Typo>
							</>
						)}
						<HBox gap="5px" sx={{flexWrap:'wrap'}}>{tabs}</HBox>
					</VBox>
					{decoration}
				</HBox>
				{spaceBottom && <Spacer size={`${spaceBottom}px`} />}

				{current}
			</>
		);
	}

	return (
		<>
			<TabStrip
				sx={{
					background: background ? background : "transparent",
					gap: variant === "modern" ? "7px" : variant === "strong" ? "0px" : !rounds ? "0px" : "10px",
					marginLeft: rounds ? (spaceLeft ? `${spaceLeft}px` : "0px") : undefined,
					...sx,
				}}>
				{spaceLeft && !rounds && <Spacer size={`${spaceLeft}px`} />}

				{tabs}

				{!rounds && (variant !== "modern" || decoration) && (
					<Box
						sx={{
							borderBottom: variant === "modern"
								? "1px solid rgba(0,0,0,.05)"
								: variant === "strong"
									? "3px solid rgba(0,0,0,.1)"
									: "2px solid rgba(0,0,0,.1)",
							flexGrow: "1",
						}}></Box>
				)}
				{decoration && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							// Match the modern row baseline so the decoration sits flush with the
							// tab buttons' lower edge instead of floating mid-row.
							borderBottom: variant === "modern" ? "1px solid rgba(0,0,0,.05)" : undefined,
							paddingLeft: "12px",
							paddingBottom: variant === "modern" ? "4px" : undefined,
						}}>
						{decoration}
					</Box>
				)}
			</TabStrip>
			{spaceBottom && <Spacer size={`${spaceBottom}px`} />}

			{current}
		</>
	);
};

export const TabItem: React.FC<ITabItemProps> = ({ children }) => {
	return <>{children}</>;
};
